import {
    Lock,
    Mic,
    Timer,
    MessageSquare,
    ShieldCheck,
    AudioLines,
    Clock,
    Pause,
    StopCircle,
    Globe2,
    MessageCircle,
    Voicemail as VoicemailIcon,
    PhoneOff,
    AlertCircle,
    HelpCircle,
    Server,
    Settings2,
    Hash
} from 'lucide-react';
import { useState, useEffect } from 'react';

import { Assistant } from "@/api/assistants/assistants.types";
import { defaultAssistantConfig } from '@/api/assistants/defaultConfig';

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { BadgeMultiSelect } from "@/components/ui/badge-multi-select";
import { SwitchWithLabel } from '@/components/ui/switch-with-label';
import { SliderWithInput } from '@/components/ui/slider-with-input';
import { NumberInput } from '@/components/ui/number-input';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

interface AdvancedTabProps {
    assistant: Omit<Assistant, 'name'>;
    onUpdate: (updates: Partial<Assistant>) => void;
    setValidationErrors?: (errors: Record<string, string> | null) => void;
}

const idleMessage = [
    { value: "Sind Sie noch da?", label: "Sind Sie noch da?" },
    { value: "Are you still there?", label: "Are you still there?" },
    { value: "Is there anything else you need help with?", label: "Is there anything else you need help with?" },
    { value: "Feel free to ask me any questions.", label: "Feel free to ask me any questions." },
    { value: "How can I assist you further?", label: "How can I assist you further?" },
    { value: "Let me know if there's anything else you need.", label: "Let me know if there's anything else you need." },
    { value: "I'm still here if you need assistance.", label: "I'm still here if you need assistance." },
    { value: "I'm ready to help whenever you are.", label: "I'm ready to help whenever you are." },
    { value: "Is there something specific you're looking for.", label: "Is there something specific you're looking for." },
    { value: "I'm here to help you with any questions you have.", label: "I'm here to help you with any questions you have." },
];

const messageTypes = [
    // { value: 'conversation-update', label: 'Conversation Update' },
    { value: 'end-of-call-report', label: 'End of Call Report' },
    // { value: 'function-call', label: 'Function Call' },
    // { value: 'hang', label: 'Hang' },
    // { value: 'model-output', label: 'Model Output' },
    // { value: 'phone-call-control', label: 'Phone Call Control' },
    // { value: 'speech-update', label: 'Speech Update' },
    // { value: 'status-update', label: 'Status Update' },
    // { value: 'transcript', label: 'Transcript' },
    // { value: 'tool-calls', label: 'Tool Calls' },
    // { value: 'transfer-destination-request', label: 'Transfer Destination Request' },
    // { value: 'user-interrupted', label: 'User Interrupted' },
    // { value: 'voice-input', label: 'Voice Input' },
];

export function AdvancedTab({ assistant, onUpdate, setValidationErrors }: AdvancedTabProps) {
    // URL validation state
    const [urlError, setUrlError] = useState<string | null>(null);
    
    // Handle both old and new server URL formats
    const getInitialServerUrl = () => {
        // Check if using the old format (directly on assistant)
        if ('serverUrl' in assistant && typeof (assistant as any).serverUrl === 'string') {
            return (assistant as any).serverUrl as string;
        }
        // New format (nested in server object)
        return assistant.server?.url || '';
    };
    
    const [serverUrl, setServerUrl] = useState<string>(getInitialServerUrl());
    const [isLegacyFormat, setIsLegacyFormat] = useState<boolean>(
        'serverUrl' in assistant && typeof (assistant as any).serverUrl === 'string'
    );
    
    // Update local state when assistant changes
    useEffect(() => {
        setServerUrl(getInitialServerUrl());
        setIsLegacyFormat('serverUrl' in assistant && typeof (assistant as any).serverUrl === 'string');
    }, [assistant]);
    
    // Expose validation errors to parent component
    useEffect(() => {
        if (setValidationErrors) {
            setValidationErrors(urlError ? { serverUrl: urlError } : null);
        }
    }, [urlError, setValidationErrors]);

    // Get default values for nested properties with fallbacks
    const startSpeakingPlan = assistant.startSpeakingPlan || defaultAssistantConfig.startSpeakingPlan;
    const transcriptionEndpointingPlan = startSpeakingPlan?.transcriptionEndpointingPlan ||
        defaultAssistantConfig.startSpeakingPlan.transcriptionEndpointingPlan;
    const stopSpeakingPlan = assistant.stopSpeakingPlan || defaultAssistantConfig.stopSpeakingPlan;
    const messagePlan = assistant.messagePlan || defaultAssistantConfig.messagePlan;

    // Ensure all values have defaults
    const waitSeconds = startSpeakingPlan?.waitSeconds ?? defaultAssistantConfig.startSpeakingPlan.waitSeconds;
    const smartEndpointingEnabled = startSpeakingPlan?.smartEndpointingEnabled ?? defaultAssistantConfig.startSpeakingPlan.smartEndpointingEnabled;
    const onPunctuationSeconds = transcriptionEndpointingPlan?.onPunctuationSeconds ?? defaultAssistantConfig.startSpeakingPlan.transcriptionEndpointingPlan.onPunctuationSeconds;
    const onNoPunctuationSeconds = transcriptionEndpointingPlan?.onNoPunctuationSeconds ?? defaultAssistantConfig.startSpeakingPlan.transcriptionEndpointingPlan.onNoPunctuationSeconds;
    const onNumberSeconds = transcriptionEndpointingPlan?.onNumberSeconds ?? defaultAssistantConfig.startSpeakingPlan.transcriptionEndpointingPlan.onNumberSeconds;
    const numWords = stopSpeakingPlan?.numWords ?? defaultAssistantConfig.stopSpeakingPlan.numWords;
    const voiceSeconds = stopSpeakingPlan?.voiceSeconds ?? defaultAssistantConfig.stopSpeakingPlan.voiceSeconds;
    const backoffSeconds = stopSpeakingPlan?.backoffSeconds ?? defaultAssistantConfig.stopSpeakingPlan.backoffSeconds;
    const silenceTimeoutSeconds = assistant.silenceTimeoutSeconds ?? defaultAssistantConfig.silenceTimeoutSeconds;
    const maxDurationSeconds = assistant.maxDurationSeconds ?? defaultAssistantConfig.maxDurationSeconds;

    const handleNestedUpdate = (
        parent: keyof Omit<Assistant, 'name'>,
        field: string,
        value: any
    ) => {
        if (field.includes('.')) {
            const [nestedParent, nestedField] = field.split('.');
            onUpdate({
                [parent]: {
                    ...(assistant[parent] as any),
                    [nestedParent]: {
                        ...(assistant[parent] as any)?.[nestedParent],
                        [nestedField]: value
                    }
                }
            } as Partial<Assistant>);
        } else {
            onUpdate({
                [parent]: {
                    ...(assistant[parent] as any),
                    [field]: value
                }
            } as Partial<Assistant>);
        }
    };

    const handlePrivacyUpdate = (field: keyof Assistant['artifactPlan'], value: any) => {
        handleNestedUpdate('artifactPlan', field, value);
    };

    const handleHipaaUpdate = (value: boolean) => {
        onUpdate({ hipaaEnabled: value });
    };

    const handleSpeakingUpdate = (field: string, value: any) => {
        if (field.startsWith('transcriptionEndpointing.')) {
            const [, nestedField] = field.split('.');
            handleNestedUpdate('startSpeakingPlan', 'transcriptionEndpointingPlan', {
                ...assistant.startSpeakingPlan?.transcriptionEndpointingPlan,
                [nestedField]: value
            });
        } else if (field.startsWith('stop.')) {
            const [, nestedField] = field.split('.');
            handleNestedUpdate('stopSpeakingPlan', nestedField, value);
        } else {
            handleNestedUpdate('startSpeakingPlan', field, value);
        }
    };

    const handleTimeoutUpdate = (field: keyof Pick<Assistant, 'silenceTimeoutSeconds' | 'maxDurationSeconds'>, value: number) => {
        onUpdate({ [field]: value });
    };

    const validateServerUrl = (url: string): boolean => {
        if (!url) return true; // Empty URL is valid (it will be cleared)
        
        const validPattern = /^https:\/\/(dev\.)?(workflow\.(amira\.bot|voicebot\.studio))\/api\/v1\/webhooks\/[a-zA-Z0-9]+(\/sync|\/async)?$/;
        return validPattern.test(url);
    };

    const handleServerUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const url = e.target.value;
        // Always update the local state to enable typing
        setServerUrl(url);
    };

    const handleServerUrlBlur = () => {
        // Validate and update server state only when focus leaves the field
        const isValidUrl = validateServerUrl(serverUrl);
        const errorMsg = serverUrl && !isValidUrl 
            ? "URL must match format: https://workflow.voicebot.studio/api/v1/webhooks/[ID]" 
            : null;
        setUrlError(errorMsg);
        
        // Handle legacy format migration
        if (isLegacyFormat) {
            // Handle legacy property with type assertion
            onUpdate({ ...(assistant as any), serverUrl: undefined } as unknown as Partial<Assistant>);
            setIsLegacyFormat(false);
        }
        
        // If URL is empty, remove the server object completely
        if (!serverUrl) {
            onUpdate({ server: undefined });
            return;
        }
        
        // Only update with valid URLs
        if (isValidUrl) {
            onUpdate({ 
                server: {
                    ...(assistant.server || {}),
                    url: serverUrl
                }
            });
        }
    };

    const handleMessagingUpdate = (field: string, value: any) => {
        if (field.startsWith('messagePlan.')) {
            const [, nestedField] = field.split('.');
            handleNestedUpdate('messagePlan', nestedField, value);
        } else if (field === 'server') {
            // Always update the server object when explicitly passed
            onUpdate({ server: value });
        } else {
            onUpdate({ [field]: value });
        }
    };

    return (
        <div className="grid grid-cols-12 gap-6">
            {/* Left Column */}
            <div className="col-span-8 space-y-6">
                {/* Speaking Settings Card */}
                <Card className="shadow-sm">
                    <CardHeader className="border-b bg-muted/20">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                <Mic className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 space-y-1">
                                <CardTitle>Speaking Settings</CardTitle>
                                <CardDescription>
                                    Configure when and how the assistant speaks
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>

                    <CardContent className="space-y-6">
                        <SliderWithInput
                            icon={Clock}
                            label="Wait Seconds"
                            subline='How long assistant waits before speaking'
                            value={waitSeconds}
                            defaultValue={defaultAssistantConfig.startSpeakingPlan.waitSeconds}
                            onChange={(value) => handleSpeakingUpdate('waitSeconds', value)}
                            min={0}
                            max={2}
                            step={0.1}
                            leftLabel='0 sec'
                            rightLabel='2 sec'
                            formatValue={(v) => v.toFixed(1)}
                            decimalScale={1}
                        />

                        <SwitchWithLabel
                            icon={Settings2}
                            label="Smart Endpointing"
                            subline="Enable for more accurate speech endpoint detection"
                            checked={smartEndpointingEnabled}
                            onCheckedChange={(checked) => handleSpeakingUpdate('smartEndpointingEnabled', checked)}
                        />

                        {!smartEndpointingEnabled && (
                            <div className="space-y-4 pt-2">
                                <SliderWithInput
                                    icon={Pause}
                                    label="On Punctuation Seconds"
                                    subline="Minimum seconds to wait after punctuation"
                                    value={onPunctuationSeconds}
                                    defaultValue={defaultAssistantConfig.startSpeakingPlan.transcriptionEndpointingPlan.onPunctuationSeconds}
                                    onChange={(value) => handleSpeakingUpdate('transcriptionEndpointing.onPunctuationSeconds', value)}
                                    min={0}
                                    max={3}
                                    step={0.1}
                                    leftLabel='0 sec'
                                    rightLabel='3 sec'
                                    formatValue={(v) => v.toFixed(1)}
                                    decimalScale={1}
                                />

                                <SliderWithInput
                                    icon={StopCircle}
                                    label="On No Punctuation Seconds"
                                    subline="Minimum seconds to wait without punctuation"
                                    value={onNoPunctuationSeconds}
                                    defaultValue={defaultAssistantConfig.startSpeakingPlan.transcriptionEndpointingPlan.onNoPunctuationSeconds}
                                    onChange={(value) => handleSpeakingUpdate('transcriptionEndpointing.onNoPunctuationSeconds', value)}
                                    min={0}
                                    max={3}
                                    step={0.1}
                                    leftLabel='0 sec'
                                    rightLabel='3 sec'
                                    formatValue={(v) => v.toFixed(1)}
                                    decimalScale={1}
                                />

                                <SliderWithInput
                                    icon={Clock}
                                    label="On Number Seconds"
                                    subline="Minimum seconds to wait after a number"
                                    value={onNumberSeconds}
                                    defaultValue={defaultAssistantConfig.startSpeakingPlan.transcriptionEndpointingPlan.onNumberSeconds}
                                    onChange={(value) => handleSpeakingUpdate('transcriptionEndpointing.onNumberSeconds', value)}
                                    min={0}
                                    max={3}
                                    step={0.1}
                                    leftLabel='0 sec'
                                    rightLabel='3 sec'
                                    formatValue={(v) => v.toFixed(1)}
                                    decimalScale={1}
                                />
                            </div>
                        )}

                        <div className="space-y-4">
                            <SliderWithInput
                                icon={Hash}
                                label="Number of Words"
                                subline="Words customer must say before assistant stops talking"
                                value={numWords}
                                defaultValue={defaultAssistantConfig.stopSpeakingPlan.numWords}
                                onChange={(value) => handleSpeakingUpdate('stop.numWords', value)}
                                min={0}
                                max={10}
                                step={1}
                                leftLabel='0'
                                rightLabel='10'
                            />

                            <SliderWithInput
                                icon={Mic}
                                label="Voice Seconds"
                                subline="Seconds customer must speak before assistant stops"
                                value={voiceSeconds}
                                defaultValue={defaultAssistantConfig.stopSpeakingPlan.voiceSeconds}
                                onChange={(value) => handleSpeakingUpdate('stop.voiceSeconds', value)}
                                min={0}
                                max={0.5}
                                step={0.1}
                                leftLabel='0 sec'
                                rightLabel='0.5 sec'
                                formatValue={(v) => v.toFixed(1)}
                                decimalScale={1}
                            />

                            <SliderWithInput
                                icon={Clock}
                                label="Backoff Seconds"
                                subline="Seconds to wait before talking again after interruption"
                                value={backoffSeconds}
                                defaultValue={defaultAssistantConfig.stopSpeakingPlan.backoffSeconds}
                                onChange={(value) => handleSpeakingUpdate('stop.backoffSeconds', value)}
                                min={0}
                                max={10}
                                step={1}
                                leftLabel='0 sec'
                                rightLabel='10 sec'
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Messaging Settings Card */}
                <Card className="shadow-sm">
                    <CardHeader className="border-b bg-muted/20">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                <MessageSquare className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 space-y-1">
                                <CardTitle>Messaging Settings</CardTitle>
                                <CardDescription>
                                    Configure server endpoints and call messages
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>

                    <CardContent className="space-y-6">
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <Globe2 className="h-4 w-4 text-primary" />
                                <Label>Server URL</Label>
                                <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-sm">
                                            <p>The endpoint URL must be in the format: https://workflow.voicebot.studio/api/v1/webhooks/[ID]</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                                {isLegacyFormat && (
                                    <span className="ml-2 text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded">
                                        Legacy format - cannot be updated
                                    </span>
                                )}
                            </div>
                            <Input
                                value={serverUrl}
                                onChange={handleServerUrlChange}
                                onBlur={handleServerUrlBlur}
                                placeholder="https://workflow.voicebot.studio/api/v1/webhooks/[ID]"
                                className={urlError ? "h-9 border-red-500" : "h-9"}
                            />
                            {urlError && (
                                <p className="text-xs text-red-500">{urlError}</p>
                            )}
                            <p className="text-xs text-muted-foreground">
                                Server endpoint URL for receiving call events and webhooks
                            </p>
                        </div>

                        <SliderWithInput
                            label="Server Timeout"
                            icon={Clock}
                            subline="Timeout in seconds for the request to your server"
                            value={assistant.server?.timeoutSeconds ?? 20}
                            defaultValue={20}
                            onChange={(value) => {
                                handleMessagingUpdate('server', {
                                    ...(assistant.server || {}),
                                    timeoutSeconds: value
                                });
                            }}
                            min={1}
                            max={120}
                            step={1}
                            leftLabel='1 sec'
                            rightLabel='120 sec'
                            tooltip="Timeout in seconds for the request to your server"
                            inputWidth={85}
                        />

                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <ShieldCheck className="h-4 w-4 text-primary" />
                                <Label>Server Secret</Label>
                                <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-sm">
                                            <p>Secret key sent as x-voicebot-secret header with every request to your server</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                            <Input
                                type="password"
                                value={assistant.server?.secret || ''}
                                onChange={(e) => {
                                    handleMessagingUpdate('server', {
                                        ...(assistant.server || {}),
                                        secret: e.target.value
                                    });
                                }}
                                placeholder="Secret key (optional)"
                                className="h-9"
                            />
                            <p className="text-xs text-muted-foreground">
                                Optional secret key for securing webhook requests
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <Server className="h-4 w-4 text-primary" />
                                <Label>Server Messages</Label>
                                <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-sm">
                                            <p>Event types that will be sent to your server endpoint</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                            <BadgeMultiSelect
                                options={messageTypes}
                                value={assistant.serverMessages}
                                onChange={(value) => handleMessagingUpdate('serverMessages', value)}
                                placeholder="Select server messages..."
                            />
                            <p className="text-xs text-muted-foreground">
                                Select which event types should be forwarded to your server
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <VoicemailIcon className="h-4 w-4 text-primary" />
                                <Label>Voicemail Message</Label>
                                <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-sm">
                                            <p>Message to speak when voicemail is detected</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                            <Input
                                value={assistant.voicemailMessage}
                                onChange={(e) => handleMessagingUpdate('voicemailMessage', e.target.value)}
                                placeholder="Hey, can you call back? Thanks!"
                                className="h-9"
                            />
                            <p className="text-xs text-muted-foreground">
                                Message spoken when voicemail is detected
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <PhoneOff className="h-4 w-4 text-primary" />
                                <Label>End Call Message</Label>
                                <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-sm">
                                            <p>Message to speak when ending the call</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                            <Input
                                value={assistant.endCallMessage}
                                onChange={(e) => handleMessagingUpdate('endCallMessage', e.target.value)}
                                placeholder="Goodbye."
                                className="h-9"
                            />
                            <p className="text-xs text-muted-foreground">
                                Message spoken before ending the call
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4" />
                                <Label>Idle Messages</Label>
                                <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-sm">
                                            <p>Messages to speak when the customer is idle</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                            <BadgeMultiSelect
                                value={messagePlan.idleMessages}
                                options={idleMessage}
                                onChange={(value) => handleMessagingUpdate('messagePlan.idleMessages', value)}
                            />
                            <p className="text-xs text-muted-foreground">
                                Messages spoken when customer is inactive
                            </p>
                        </div>

                        <div className="space-y-4">
                            <SliderWithInput
                                icon={Hash}
                                label="Max Idle Messages"
                                subline="Maximum number of times idle messages can be spoken"
                                value={messagePlan.idleMessageMaxSpokenCount}
                                defaultValue={defaultAssistantConfig.messagePlan.idleMessageMaxSpokenCount}
                                onChange={(value) => handleMessagingUpdate('messagePlan.idleMessageMaxSpokenCount', value)}
                                min={1}
                                max={10}
                                step={1}
                                leftLabel='1'
                                rightLabel='10'
                            />

                            <SliderWithInput
                                icon={Clock}
                                label="Idle Timeout"
                                subline="Timeout in seconds before an idle message is spoken"
                                value={messagePlan.idleTimeoutSeconds}
                                defaultValue={defaultAssistantConfig.messagePlan.idleTimeoutSeconds}
                                onChange={(value) => handleMessagingUpdate('messagePlan.idleTimeoutSeconds', value)}
                                min={5}
                                max={60}
                                step={0.1}
                                leftLabel='5 sec'
                                rightLabel='60 sec'
                                formatValue={(v) => v.toFixed(1)}
                                decimalScale={1}
                                inputWidth={90}
                            />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Right Column */}
            <div className="col-span-4 space-y-6">
                {/* Privacy & Recording Card */}
                <Card className="shadow-sm">
                    <CardHeader className="border-b bg-muted/20">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                <Lock className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 space-y-1">
                                <CardTitle>Privacy & Recording</CardTitle>
                                <CardDescription>
                                    HIPAA compliance and recording settings
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>

                    <CardContent className="space-y-6">
                        <SwitchWithLabel
                            icon={ShieldCheck}
                            label="HIPAA Compliance"
                            subline="When this is enabled, no logs, recordings, or transcriptions will be stored."
                            tooltip="HIPAA Compliance is enforced at the organization level and overrides individual settings."
                            checked={assistant.hipaaEnabled}
                            onCheckedChange={handleHipaaUpdate}
                        />

                        <SwitchWithLabel
                            icon={AudioLines}
                            label="Audio Recording"
                            subline="Record the conversation with the assistant."
                            checked={assistant.artifactPlan.recordingEnabled}
                            onCheckedChange={(checked) => handlePrivacyUpdate('recordingEnabled', checked)}
                            disabled={assistant.hipaaEnabled}
                        />

                        {assistant.hipaaEnabled && (
                            <Alert className='bg-yellow-100 border-yellow-300 text-yellow-800'>
                                <AlertDescription className="flex items-center">
                                    <AlertCircle className="h-5 w-5 text-yellow-500 inline-block mr-2" />
                                    HIPAA compliance is enabled. Recording settings are disabled.
                                </AlertDescription>
                            </Alert>
                        )}
                    </CardContent>
                </Card>

                {/* Call Timeout Settings Card */}
                <Card className="shadow-sm">
                    <CardHeader className="border-b bg-muted/20">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                <Timer className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 space-y-1">
                                <CardTitle>Call Timeout Settings</CardTitle>
                                <CardDescription>
                                    Configure call timeout durations
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>

                    <CardContent className="space-y-4">
                        <SliderWithInput
                            icon={AlertCircle}
                            label="Silence Timeout"
                            subline="Seconds before call ends due to inactivity"
                            value={silenceTimeoutSeconds}
                            defaultValue={defaultAssistantConfig.silenceTimeoutSeconds}
                            onChange={(value) => handleTimeoutUpdate('silenceTimeoutSeconds', value)}
                            min={10}
                            max={600}
                            step={1}
                            leftLabel='10 sec'
                            rightLabel='600 sec'
                            inputWidth={85}
                        />

                        <SliderWithInput
                            icon={Clock}
                            label="Maximum Duration"
                            subline="Maximum number of seconds a call will last"
                            value={maxDurationSeconds}
                            defaultValue={defaultAssistantConfig.maxDurationSeconds}
                            onChange={(value) => handleTimeoutUpdate('maxDurationSeconds', value)}
                            min={10}
                            max={43200}
                            step={1}
                            leftLabel='10 sec'
                            rightLabel='43200 sec'
                            inputWidth={105}
                        />
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
